

import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen,
  Leaf,
  RefreshCw,
  Settings,
  Zap,
  Users,
  MessageSquare,
  FileText,
  Code,
  Languages,
  Calculator,
  Dna
} from "lucide-react";
import { useI18n } from "@/i18n";

const apps = [
  // GRASP! Education Series - Featured
  {
    icon: Dna,
    name: "GRASP! Biology Tutor",
    nameCn: "GRASP! 生物导师",
    description: "O-Level Biology aligned with Singapore syllabus",
    descriptionCn: "新加坡 O-Level 生物课程导师",
    featured: true,
  },
  {
    icon: Leaf,
    name: "GRASP! Science: Diversity",
    nameCn: "GRASP! 科学：多样性",
    description: "Primary Science - Living & non-living things",
    descriptionCn: "小学科学 - 生物与非生物分类",
    featured: true,
  },
  {
    icon: RefreshCw,
    name: "GRASP! Science: Cycles",
    nameCn: "GRASP! 科学：循环",
    description: "Primary Science - Life cycles & matter states",
    descriptionCn: "小学科学 - 生命周期与物质状态",
    featured: true,
  },
  {
    icon: Settings,
    name: "GRASP! Science: Systems",
    nameCn: "GRASP! 科学：系统",
    description: "Primary Science - Plant, human & electrical systems",
    descriptionCn: "小学科学 - 植物、人体与电路系统",
    featured: true,
  },
  {
    icon: Zap,
    name: "GRASP! Science: Energy",
    nameCn: "GRASP! 科学：能量",
    description: "Primary Science - Light, heat & photosynthesis",
    descriptionCn: "小学科学 - 光、热与光合作用",
    featured: true,
  },
  {
    icon: Users,
    name: "GRASP! Science: Interactions",
    nameCn: "GRASP! 科学：相互作用",
    description: "Primary Science - Ecosystems, forces & adaptations",
    descriptionCn: "小学科学 - 生态系统、力与适应性",
    featured: true,
  },
  // General Purpose Apps
  {
    icon: MessageSquare,
    name: "AI Chat",
    nameCn: "AI 聊天",
    description: "Conversational AI assistant",
    descriptionCn: "智能对话助手",
    featured: false,
  },
  {
    icon: FileText,
    name: "Writing Assistant",
    nameCn: "写作助手",
    description: "Content generation & editing",
    descriptionCn: "内容生成与编辑",
    featured: false,
  },
  {
    icon: Code,
    name: "Code Helper",
    nameCn: "编程助手",
    description: "AI-powered coding companion",
    descriptionCn: "AI 编程助手",
    featured: false,
  },
  {
    icon: Languages,
    name: "Translator",
    nameCn: "翻译工具",
    description: "Translate between languages",
    descriptionCn: "多语言翻译",
    featured: false,
  },
  {
    icon: Calculator,
    name: "Math Solver",
    nameCn: "数学求解",
    description: "Solve complex math problems",
    descriptionCn: "数学问题求解",
    featured: false,
  },
  {
    icon: BookOpen,
    name: "Study Buddy",
    nameCn: "学习伙伴",
    description: "Learn and study with AI",
    descriptionCn: "AI 学习助手",
    featured: false,
  },
];

const AppShowcase = () => {
  const { language } = useI18n();

  return (
    <section id="apps" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              {language === 'en' ? 'Your Personal AI App Store' : '您的个人 AI 应用商店'}
            </h2>
            <p className="text-lg text-gray-600">
              {language === 'en'
                ? 'Explore AI assistants, Chat Apps, creative tools, and more—all running locally.'
                : '浏览 AI 助手、对话应用、创作工具等，全部本地运行。'
              }
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((app, index) => (
              <Card
                key={index}
                className={`hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer relative bg-white border-gray-200 ${
                  app.featured ? 'ring-2 ring-blue-200 bg-blue-50' : ''
                }`}
              >
                {app.featured && (
                  <Badge className="absolute top-3 right-3 bg-blue-600 text-white">
                    {language === 'en' ? 'Featured' : '精选'}
                  </Badge>
                )}
                <CardHeader>
                  <div className={`mb-4 inline-flex p-3 rounded-xl ${
                    app.featured ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <app.icon className={`w-6 h-6 ${
                      app.featured ? 'text-blue-600' : 'text-gray-700'
                    }`} />
                  </div>
                  <CardTitle className="text-xl text-gray-900">
                    {language === 'en' ? app.name : app.nameCn}
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    {language === 'en' ? app.description : app.descriptionCn}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-xl md:text-2xl font-medium text-gray-600">
              {language === 'en' ? 'and many more...' : '还有更多...'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppShowcase;
